<template>
  <div>
    <div v-if="!isAuthenticated">
      <h1>Вхід</h1>
      <p v-if="loginError" class="error">{{ loginError }}</p>
      <form @submit.prevent="login" class="login-form">
        <label for="password">Пароль:</label>
        <input type="password" id="password" v-model="password" placeholder="Введіть пароль" required />
        <button type="submit" :disabled="isLoggingIn">
          <span v-if="isLoggingIn">Вхід...</span>
          <span v-else>Увійти</span>
        </button>
      </form>
    </div>
    <div v-else>
      <h1>Завантажити аудіо</h1>
      <div class="button-container">
        <label class="file-label">
          <input type="file" @change="onFileChange" accept=".mp3,.mp4,.mpeg,.mpga,.m4a,.wav,.webm,.aac,.flac" />
          Вибрати аудіофайл файл
        </label>
        <button @click="uploadAudio" :disabled="isLoading || !audioFile">
          <span v-if="isLoading">Завантаження...</span>
          <span v-else>Завантажити</span>
        </button>
      </div>

      <div v-if="selectedFileName" class="file-name">
        Обраний файл: {{ selectedFileName }}
      </div>

      <div v-if="isLoading" class="loading">
        Завантаження та обробка аудіо...
      </div>

      <div v-if="response" class="response-container">
        <h2>Відповідь сервера:</h2>
        <json-text :text="response"></json-text>
      </div>

      <div v-if="error" class="error">
        <p>Помилка: {{ error }}</p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, Ref, onMounted } from 'vue';
import axios from 'axios';
import { sendAudio } from '../services/api';
import JsonText from './JsonText.vue';

const supportedTypes = [
  'audio/mpeg',
  'audio/mp4',
  'audio/mpga',
  'audio/m4a',
  'audio/wav',
  'audio/flac',
  'audio/webm',
  'audio/aac',
  'audio/vnd.dlna.adts',
];

export default defineComponent({
  components: {
    JsonText,
  },
  setup() {
    const audioFile = ref<File | null>(null);
    const selectedFileName = ref<string | null>(null);
    const response = ref<any>(null);
    const error = ref<string | null>(null);
    const isLoading = ref(false);
    const isLoggingIn = ref(false);

    const password = ref('');
    const isAuthenticated = ref(false);
    const loginError = ref<string | null>(null);

    onMounted(() => {
      checkAuthentication(isAuthenticated);
    });

    async function checkAuthentication(isAuthenticated:Ref<boolean>): Promise<void> {
      try {
         //Get Token
        const token = localStorage.getItem('authToken');
        const baseURL = process.env.VUE_APP_BACKEND_URL;

        const response = await axios.get(`${baseURL}/api/check-auth`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          validateStatus: (status) => status >= 200 && status < 300 || status === 401,
        });
        //  console.log("checkAuthentication: success", response);
        //The code was checking the status code, but not verifying anything with the function!
        isAuthenticated.value = response.status === 200;
      } catch (err: any) {
        // console.error('checkAuthentication: Error:', err);
        isAuthenticated.value = false;
      }
    }

    const login = async () => {
      loginError.value = null;
      isLoggingIn.value = true;
      try {
        const baseURL = process.env.VUE_APP_BACKEND_URL;
        const formData = new URLSearchParams();
        formData.append('password', password.value);

        const response = await axios.post(`${baseURL}/login`, formData, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          withCredentials: true,
          validateStatus: (status) => status >= 200 && status < 300 || status === 401,
        });

        if (response.status === 200) {
          const { token } = response.data;
          localStorage.setItem('authToken', token);
          isAuthenticated.value = true;
          password.value = '';
        } else {
          loginError.value = 'Невірний пароль.';
          isAuthenticated.value = false;
        }
      } catch (err: any) {
        console.error('Login error:', err);
        loginError.value = 'Помилка входу. Будь ласка, спробуйте ще раз.';
        isAuthenticated.value = false;
      } finally {
        isLoggingIn.value = false;
      }
    };

    const onFileChange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const file = target.files[0];

        if (supportedTypes.includes(file.type)) {
          audioFile.value = file;
          selectedFileName.value = file.name;
        } else {
          alert('Будь ласка, оберіть аудіо файл.');
          target.value = '';
          audioFile.value = null;
          selectedFileName.value = null;
        }

      } else {
        audioFile.value = null;
        selectedFileName.value = null;
      }
    };

    const uploadAudio = async () => {
      if (!audioFile.value) return;

      try {
        isLoading.value = true;
        error.value = null;
        response.value = null;

        const res = await sendAudio(audioFile.value);

        response.value = res;
      } catch (err: any) {
        console.error('Error uploading audio:', err);
        error.value = 'Виникла помилка під час обробки аудіо. Будь ласка, спробуйте ще раз.';
      } finally {
        isLoading.value = false;
      }
    };

    return {
      onFileChange,
      uploadAudio,
      response,
      error,
      isLoading,
      selectedFileName,
      password,
      isAuthenticated,
      loginError,
      login,
      audioFile,
      isLoggingIn,
    };
  },
});
</script>

<style scoped>

h1 {
  text-align: center;
}

.login-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
}

.login-form label {
  font-weight: bold;
}

.button-container {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.file-label,
button {
  padding: 5px 10px;
  border: none;
  background-color: #007BFF;
  color: white;
  cursor: pointer;
  border-radius: 3px;
  text-align: center;
  font-size: 16px;
}

.file-label:hover,
button:hover {
  background-color: #0056b3;
}

input[type="file"] {
  display: none;
}

.file-name {
  text-align: center;
  margin-top: 10px;
  font-style: italic;
}

.loading {
  text-align: center;
  margin: 20px;
  color: #007BFF;
}

.response-container,
.error {
  border: 1px solid #ccc;
  padding: 10px;
  border-radius: 5px;
  margin: 20px auto;
  max-width: 600px;
}

.error {
  color: red;
  background-color: #fdd;
}
</style>